/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import {
  CSRF_COOKIE_NAME,
  CSRF_HEADER_NAME,
  getCsrfHeader as getCommonCsrfHeader,
  getJwtJti,
} from "troy-common/utils/csrf"
import { storageHelper } from "./storage"

/**
 * CSRF transport, delegated to the shared implementation in troy-common
 * (troy-portal v202609211). That module owns the cookie/header names, the
 * state-changing method set and the `jti` extraction; this file only binds the
 * two portal-specific inputs it takes — the cookie-auth gate and the JWT — to
 * this app's `storageHelper`, so the frontend no longer keeps its own cookie
 * getter/setter that could drift from the other portals.
 *
 * The `jti` comes from the `mexusers_authn_token_jti` cookie under cookie auth
 * and from the `jti` claim of the JWT otherwise (the injected-session WebView
 * flow), so both transports send the same X-CSRF-Token.
 */
export { CSRF_COOKIE_NAME, CSRF_HEADER_NAME, getJwtJti }

// Mirror the auth token's `jti` into X-CSRF-Token for state-changing requests.
// `isCookieAuth` and `jwt` default to this app's current transport so existing
// `getCsrfHeader(method)` call sites keep working; callers that already hold
// their own transport decision (the trx-common HTTPRequestFactory) pass them
// explicitly. Returns an empty object when the method is read-only or no token
// is available — the backend/API Gateway remains the authoritative 403 enforcer.
export const getCsrfHeader = (
  method?: string,
  isCookieAuth: boolean = storageHelper.isCookieAuthEnabled(),
  jwt: string | null = storageHelper.getToken(),
): Record<string, string> =>
  getCommonCsrfHeader(method, isCookieAuth, jwt) as Record<string, string>
